/**
 * Outbox, audit, timeline and reporting (docs/PLAN.md §4 PR 15, PRD AC19/AC20).
 *
 * Every named command writes, in one transaction, the domain change, an `audit_events` row
 * (who did what to which record) and an `outbox_events` row (what the rest of the system may
 * react to). Nothing else observes those rows directly: a worker drains the outbox, turning
 * each domain event into zero or more *notifications* — emails to the people on the case
 * whose text carries only a safe status and an authenticated link, never amounts, terms or
 * application answers (AC19). Delivery is at-least-once with retries; duplicates are made
 * impossible by a per-recipient dedupe key on the notification row, not by hoping the retry
 * never fires.
 *
 * `case_events` is the human-readable timeline of a case and each row names the audience that
 * may see it; `audit_events` is the forensic record and is Tex-only (platform-wide: ops only).
 * Reporting is *counts* over stages and close reasons (AC20); rates, durations and cohorts
 * are P1 and deliberately absent.
 */

import type { CaseStage } from './api-types'

// ── closing a case (Tex) ─────────────────────────────────────────────────────

export const CLOSE_REASONS = [
  'borrower_withdrew',
  'dealer_withdrew',
  'no_offer',
  'out_of_scope_referred',
  'duplicate',
  'expired',
  'declined_by_partner',
  'other',
] as const
export type CloseReason = (typeof CLOSE_REASONS)[number]

/** Reasons Tex may close a case with directly; withdrawals go through `POST .../withdraw` on the requester's word. */
export const TEX_CLOSE_REASONS = [
  'borrower_withdrew',
  'dealer_withdrew',
  'no_offer',
  'out_of_scope_referred',
  'duplicate',
  'expired',
  'declined_by_partner',
  'other',
] as const satisfies readonly CloseReason[]

export const REFERRAL_CATEGORIES = [
  'cash_out',
  'debt_restructuring',
  'receivables',
  'robot_deployment',
  'venture_equipment',
  'private_party',
  'other',
] as const
export type ReferralCategory = (typeof REFERRAL_CATEGORIES)[number]

export interface CloseCaseBody {
  expected_version: number
  close_reason: CloseReason
  /** Required with `out_of_scope_referred`; refused with any other reason. */
  referral_category?: ReferralCategory | null
  /** Who the buyer was pointed at (a firm or specialist), for `out_of_scope_referred` only. */
  referred_to?: string | null
  note: string
}

// ── timeline (case_events) ───────────────────────────────────────────────────

export const CASE_EVENT_VISIBILITIES = ['tex', 'dealer', 'borrower', 'all'] as const
export type CaseEventVisibility = (typeof CASE_EVENT_VISIBILITIES)[number]

export const CASE_EVENT_ACTOR_TYPES = ['dealer', 'borrower', 'tex', 'system', 'partner'] as const
export type CaseEventActorType = (typeof CASE_EVENT_ACTOR_TYPES)[number]

export interface CaseTimelineEventView {
  id: string
  type: string
  actor_type: CaseEventActorType
  /** Tex sees the user id; dealer and borrower projections omit it. */
  actor_user_id?: string | null
  from_stage: CaseStage | null
  to_stage: CaseStage | null
  visibility: CaseEventVisibility
  summary: string
  /** Tex only. Dealer and borrower projections never carry metadata. */
  metadata?: Record<string, unknown>
  created_at: string
}

export interface CaseTimelineResponse {
  case_id: string
  events: CaseTimelineEventView[]
  next_cursor: string | null
}

// ── audit (audit_events) ─────────────────────────────────────────────────────

export const AUDIT_ACTOR_TYPES = ['user', 'participant', 'system'] as const
export type AuditActorType = (typeof AUDIT_ACTOR_TYPES)[number]

export interface AuditEventView {
  id: string
  action: string
  actor_type: AuditActorType
  actor_id: string | null
  actor_organization_id: string | null
  target_type: string
  target_id: string
  case_id: string | null
  correlation_id: string | null
  metadata: Record<string, unknown>
  created_at: string
}

export interface AuditListResponse {
  events: AuditEventView[]
  next_cursor: string | null
}

// ── outbox (worker + notifications) ──────────────────────────────────────────

export const OUTBOX_STATUSES = ['queued', 'sent', 'failed', 'suppressed'] as const
export type OutboxStatus = (typeof OUTBOX_STATUSES)[number]

export interface OutboxEventView {
  id: string
  topic: string
  dedupe_key: string | null
  status: OutboxStatus
  attempt_count: number
  next_attempt_at: string
  last_error: string | null
  sent_at: string | null
  correlation_id: string | null
  created_at: string
}

export interface OutboxSummaryView {
  counts: Record<OutboxStatus, number>
  /** Queued rows whose `next_attempt_at` is already due — a growing number means the worker is not running. */
  due: number
  oldest_due_at: string | null
}

export interface OutboxEventsResponse {
  summary: OutboxSummaryView
  events: OutboxEventView[]
  next_cursor: string | null
}

// ── reporting ────────────────────────────────────────────────────────────────

export interface FunnelStageCount {
  stage: CaseStage
  /** Cases currently in this stage. */
  current: number
  /** Cases that entered this stage at least once in the window (from `case_events.to_stage`). */
  entered_in_window: number
}

export interface FunnelReport {
  /** ISO timestamps; the window applies to `entered_in_window`, close reasons and referrals. `current` is as of now. */
  window: { from: string; to: string }
  dealer_id: string | null
  stages: FunnelStageCount[]
  created_in_window: number
  closed_in_window: number
  close_reasons: Array<{ close_reason: CloseReason; count: number }>
  referrals: Array<{ referral_category: ReferralCategory; count: number }>
  generated_at: string
}

/** Columns of the case-events CSV, in order. The export is audited and carries no application data. */
export const CASE_EVENTS_CSV_COLUMNS = [
  'occurred_at',
  'case_reference',
  'case_id',
  'dealer_id',
  'event_type',
  'actor_type',
  'from_stage',
  'to_stage',
  'visibility',
  'summary',
] as const
export type CaseEventsCsvColumn = (typeof CASE_EVENTS_CSV_COLUMNS)[number]
